// Per-platform profit math. Fees vary by marketplace, so every estimate is
// computed from that site's cost profile plus the shared cost assumptions.

export interface FeeProfile {
  platform: string;
  commissionPct: number;
  fixedFee: number;
  paymentPct: number;
  paymentFixed: number;
  lowPriceThreshold: number | null;
  lowPriceFee: number | null;
  sellerPaysShipping: boolean;
  /** Extra % of price when the sale came through a boosted listing (Depop 12). */
  boostPct: number;
}

export interface CostBasis {
  purchasePrice: number;
  packaging: number;
  shipping: number;
  buyerPaysShipping: boolean;
}

/** What the platform takes on a sale at `price`. */
export function platformFee(price: number, p: FeeProfile): number {
  const base =
    p.lowPriceThreshold != null && p.lowPriceFee != null && price < p.lowPriceThreshold
      ? p.lowPriceFee
      : price * (p.commissionPct / 100) + p.fixedFee;
  return base + price * (p.paymentPct / 100) + p.paymentFixed;
}

/** Estimated take-home: sale price minus platform fees minus your outlay. */
export function estimateProfit(price: number, p: FeeProfile, cost: CostBasis): number {
  const sellerShips = p.sellerPaysShipping || !cost.buyerPaysShipping;
  const outlay = cost.purchasePrice + cost.packaging + (sellerShips ? cost.shipping : 0);
  return price - platformFee(price, p) - outlay;
}

/** Numeric API values arrive as strings; normalize into a FeeProfile. */
export function parseFee(raw: Record<string, unknown>): FeeProfile {
  const n = (v: unknown) => (v == null ? null : Number(v));
  return {
    platform: String(raw.platform),
    commissionPct: Number(raw.commissionPct ?? 0),
    fixedFee: Number(raw.fixedFee ?? 0),
    paymentPct: Number(raw.paymentPct ?? 0),
    paymentFixed: Number(raw.paymentFixed ?? 0),
    lowPriceThreshold: n(raw.lowPriceThreshold),
    lowPriceFee: n(raw.lowPriceFee),
    sellerPaysShipping: !!raw.sellerPaysShipping,
    boostPct: Number(raw.boostPct ?? 0),
  };
}
